import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { OutlookDataService } from '../../services/outlook-data.service';
import { IOutlookContact } from '../../models/IOutlookContact.interface';
import { PhonebookSidebarService } from '../../../phonebook/services/phonebook-sidebar.service';
import { VisibleOnceDirective } from '../../utils/visible-once.directive';

/**
 * The signed-in user's Outlook contacts.
 *
 * Filtering is client-side: the whole address book is fetched once and cached, and five hundred
 * rows is small enough that a round trip per keystroke would be the slower option.
 */
@Component({
  selector: 'app-outlook-contacts-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    VisibleOnceDirective
  ],
  templateUrl: './outlook-contacts-tab.component.html',
  styleUrls: ['./outlook-contacts-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlookContactsTabComponent implements OnInit {
  private readonly data = inject(OutlookDataService);
  private readonly phonebook = inject(PhonebookSidebarService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  contacts: IOutlookContact[] = [];
  filtered: IOutlookContact[] = [];
  loading = false;

  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  /** Ids whose photo has already been requested, so scrolling past a row twice does not refetch. */
  private photoRequested = new Set<string>();

  ngOnInit(): void {
    this.load();

    this.searchControl.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyFilter());
  }

  refresh(): void {
    this.photoRequested.clear();
    this.load(true);
  }

  /**
   * Resolves a row's photo the first time it is asked for.
   *
   * Called from the template as rows render rather than up front, because Graph serves exactly one
   * photo per request — eagerly fetching an entire address book would be hundreds of round trips
   * for rows most people never scroll to.
   */
  ensurePhoto(contact: IOutlookContact): void {
    if (contact.photoUrl || this.photoRequested.has(contact.id)) {
      return;
    }
    this.photoRequested.add(contact.id);

    this.data
      .getContactPhoto(contact.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url) => {
        if (url) {
          contact.photoUrl = url;
          this.cdr.markForCheck();
        }
      });
  }

  openPhonebook(): void {
    this.phonebook.open();
  }

  trackById(_index: number, item: IOutlookContact): string {
    return item.id;
  }

  private load(force = false): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.data
      .getContacts(force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contacts) => {
        this.contacts = contacts;
        this.applyFilter();
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  private applyFilter(): void {
    const term = this.searchControl.value.trim().toLowerCase();
    if (!term) {
      this.filtered = this.contacts;
    } else {
      this.filtered = this.contacts.filter((c) =>
        [c.displayName, c.email, c.companyName, c.jobTitle, c.mobilePhone, c.businessPhone]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term))
      );
    }
    this.cdr.markForCheck();
  }
}
